import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import UpgradeConfirmationEmail from './emails/UpgradeConfirmation';

const EmailPreviewUpgradeConfirmation = () => {
  const navigate = useNavigate();
  const [purchaseType, setPurchaseType] = useState<'boost' | 'subscription'>('subscription');
  const [tier, setTier] = useState<'premium_economy' | 'first_class'>('premium_economy');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [boostDuration, setBoostDuration] = useState<'3' | '7' | '14'>('7');

  const getPrice = () => {
    if (purchaseType === 'boost') {
      const boostPrices: Record<string, Record<string, string>> = {
        '3': { premium_economy: '€3.49', first_class: '€5.99' },
        '7': { premium_economy: '€5.99', first_class: '€10.99' },
        '14': { premium_economy: '€9.99', first_class: '€16.99' }
      };
      return boostPrices[boostDuration][tier];
    } else {
      const subscriptionPrices: Record<string, Record<string, string>> = {
        monthly: { premium_economy: '€6.99', first_class: '€11.99' },
        annual: { premium_economy: '€69.90', first_class: '€119.90' }
      };
      return subscriptionPrices[billingPeriod][tier];
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 min-h-[44px] h-8 w-auto md:h-10 md:w-auto px-3 md:px-4 touch-manipulation text-xs sm:text-sm"
            >
              <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
            <h1 className="text-xs sm:text-sm md:text-lg font-semibold truncate">Email Preview</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <div className="bg-card rounded-lg border border-border p-3 sm:p-4 md:p-6 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-3 sm:mb-4">Email Configuration</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="purchaseType" className="text-xs sm:text-sm">Purchase Type</Label>
              <Select value={purchaseType} onValueChange={(value: 'boost' | 'subscription') => setPurchaseType(value)}>
                <SelectTrigger id="purchaseType" className="h-10 sm:h-11 text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="boost">Boost Pass</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tier" className="text-xs sm:text-sm">Tier</Label>
              <Select value={tier} onValueChange={(value: 'premium_economy' | 'first_class') => setTier(value)}>
                <SelectTrigger id="tier" className="h-10 sm:h-11 text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium_economy">Premium Economy</SelectItem>
                  <SelectItem value="first_class">First Class</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {purchaseType === 'subscription' ? (
              <div className="space-y-2">
                <Label htmlFor="billingPeriod" className="text-xs sm:text-sm">Billing Period</Label>
                <Select value={billingPeriod} onValueChange={(value: 'monthly' | 'annual') => setBillingPeriod(value)}>
                  <SelectTrigger id="billingPeriod" className="h-10 sm:h-11 text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="boostDuration" className="text-xs sm:text-sm">Boost Duration</Label>
                <Select value={boostDuration} onValueChange={(value: '3' | '7' | '14') => setBoostDuration(value)}>
                  <SelectTrigger id="boostDuration" className="h-10 sm:h-11 text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Days</SelectItem>
                    <SelectItem value="7">7 Days</SelectItem>
                    <SelectItem value="14">14 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">Price</Label>
              <div className="h-10 sm:h-11 px-3 py-2 border border-input rounded-md bg-muted flex items-center">
                <span className="text-xs sm:text-sm font-semibold">{getPrice()}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg overflow-hidden">
          <UpgradeConfirmationEmail
            purchaseType={purchaseType}
            tier={tier}
            billingPeriod={purchaseType === 'subscription' ? billingPeriod : undefined}
            boostDuration={purchaseType === 'boost' ? boostDuration : undefined}
            price={getPrice()}
            activationDate={new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          />
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewUpgradeConfirmation;